import { type DateFieldStateOptions, useDatePickerState } from "@react-stately/datepicker";
import cx from "classnames";
import { type FC, useCallback, useRef } from "react";
import { type AriaDatePickerProps, type DateValue, useDatePicker } from "react-aria";

import { WarningAltIcon } from "@moment/icons";

import { Calendar } from "../Calendar";
import { type InputDateSize } from "../InputDate";
import { makePointerStyles, makeTextStyles } from "../utilities";
import { CalendarIconButton } from "./CalendarIconButton";
import { CalendarPopover } from "./CalendarPopover";
import { CalendarPopoverDialog } from "./CalendarPopoverDialog";
import { InputDateBare } from "./InputDateBare";
import { datePickerGroupClassname } from "./common";

export type BaseDatePickerProps = Omit<
	DateFieldStateOptions,
	"locale" | "createCalendar" | "isDisabled"
> &
	Omit<AriaDatePickerProps<DateValue>, "isDisabled"> & {
		disabled?: boolean;
		size?: InputDateSize;
		visibleDurationMonths?: 1 | 2;
		ranged?: false;
		type?: "standalone" | "embedded";
		dialogTrigger?: "manual" | "click";
		hideTriggerButton?: boolean;
		withPortal?: boolean;
		constantWidthDateSegments?: boolean;
	};

export const BaseDatePicker: FC<BaseDatePickerProps> = ({
	size = "sm",
	type = "standalone",
	dialogTrigger = "manual",
	hideTriggerButton = false,
	disabled = false,
	visibleDurationMonths = 1,
	withPortal = true,
	constantWidthDateSegments = true,
	...props
}) => {
	props = Object.assign(props, { isDisabled: disabled });

	const state = useDatePickerState(props);
	const ref = useRef<HTMLDivElement>(null);
	const { groupProps, labelProps, fieldProps, buttonProps, dialogProps, calendarProps } =
		useDatePicker(props, state, ref);

	const onClickSegment = useCallback(() => {
		if (dialogTrigger === "click") {
			state.open();
		}
	}, [dialogTrigger, state]);

	return (
		<div className="relative inline-flex flex-col space-y-1 text-left">
			{props.label && (
				<span
					{...labelProps}
					className={cx(makeTextStyles(disabled, size), makePointerStyles(disabled))}
				>
					{props.label}
				</span>
			)}
			<div
				{...groupProps}
				ref={ref}
				className={cx(
					makePointerStyles(disabled),
					datePickerGroupClassname(disabled, size, type)
				)}
			>
				<div className="flex items-center">
					<InputDateBare
						datePickerDialogTrigger={dialogTrigger}
						triggerDatePickerDialog={onClickSegment}
						constantWidthDateSegments={constantWidthDateSegments}
						{...fieldProps}
					/>
					{state.validationState === "invalid" && (
						<WarningAltIcon className="absolute right-1 h-6 w-6 text-negative" />
					)}
				</div>
				{!hideTriggerButton && (
					<CalendarIconButton
						size={size}
						{...buttonProps}
						disabled={disabled}
						isPressed={state.isOpen}
					/>
				)}
			</div>
			{state.isOpen && !props.isReadOnly && !disabled && (
				<CalendarPopover
					triggerRef={ref}
					state={state}
					placement="bottom start"
					withPortal={withPortal}
				>
					<CalendarPopoverDialog {...dialogProps}>
						<Calendar
							{...calendarProps}
							visibleDurationMonths={visibleDurationMonths}
						/>
					</CalendarPopoverDialog>
				</CalendarPopover>
			)}
		</div>
	);
};
